import { catchError, of } from "rxjs";
import { Component } from "@angular/core";
import { CommonModule, Location } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import dayjs from "dayjs";

import { GloButtonComponent } from "../../../../shared/components/glo-button/glo-button.component";
import { GoogleMapComponent } from "../../../landing/shared/google-map/google-map.component";
import { AppointmentsService } from "../../../../shared/services/user-appointments.service";
import { Appointment } from "../../../../shared/models/appointment.model";
import { Salon } from "../../../../shared/models/salon.model";
import { Service } from "../../../../shared/models/service.model";
import { LoaderComponent } from "../../../../shared/components/loader/loader.component";
import { environment } from "../../../../../environments/environment";

interface Services {
  id: string;
  service: Service;
  price_snapshot: string;
  duration_snapshot: number;
  quantity: number;
}

@Component({
  selector: "customer-appointment-details",
  standalone: true,
  imports: [
    CommonModule,
    GloButtonComponent,
    GoogleMapComponent,
    LoaderComponent,
  ],
  templateUrl: "./details.component.html",
  styleUrl: "./details.component.scss",
})
export class CustomerAppointmentDetailsComponent {
  latitude = 53.3308;
  longitude = -6.2796;

  urlID: string | null = null;

  isLoading = false;
  isCancelling = false;

  showCancelConfirmation = false;

  appointmentDetails: Appointment = {} as Appointment;
  Services: Services[] = [];
  Salon: Salon = {} as Salon;

  assetUrl = environment.assetBaseUrl;

  mapMarkers = [
    {
      lat: this.latitude,
      lng: this.longitude,
    },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apptService: AppointmentsService,
    private location: Location,
  ) {}

  ngOnInit(): void {
    this.urlID = this.route.snapshot.paramMap.get("id");

    if (!this.urlID) {
      return;
    }

    this.getAppointmentDetails(this.urlID);
  }

  /* =====================================================
     APPOINTMENT
  ====================================================== */

  getAppointmentDetails(id: string | null): void {
    if (!id) {
      return;
    }

    this.isLoading = true;

    this.apptService
      .getAppointmentByID(id)
      .pipe(
        catchError((error) => {
          console.error("Failed to load appointment details:", error);

          this.isLoading = false;

          return of({} as Appointment);
        }),
      )
      .subscribe({
        next: (data: any) => {
          this.appointmentDetails = data || ({} as Appointment);

          this.Services = data?.items || [];

          this.Salon = data?.salon || ({} as Salon);

          const latitude = Number(data?.salon?.latitude);

          const longitude = Number(data?.salon?.longitude);

          if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
            this.latitude = latitude;
            this.longitude = longitude;

            this.mapMarkers = [
              {
                lat: latitude,
                lng: longitude,
              },
            ];
          }

          this.isLoading = false;
        },

        error: (error) => {
          this.isLoading = false;

          console.error("Appointment error:", error);
        },
      });
  }

  /* =====================================================
     MANAGEMENT
  ====================================================== */

  get canManageAppointment(): boolean {
    const status = String(this.appointmentDetails?.status || "").toLowerCase();

    return !["cancelled", "completed", "no_show"].includes(status);
  }

  modifyBooking(): void {
    if (!this.urlID || !this.canManageAppointment) {
      return;
    }

    this.router.navigate(["/admin/customer/appointment/modify", this.urlID]);
  }

  openCancelConfirmation(): void {
    if (!this.canManageAppointment) {
      return;
    }

    this.showCancelConfirmation = true;
  }

  closeCancelConfirmation(): void {
    if (this.isCancelling) {
      return;
    }

    this.showCancelConfirmation = false;
  }

  confirmCancellation(): void {
    if (!this.urlID || this.isCancelling) {
      return;
    }

    this.isCancelling = true;

    this.apptService.cancelAppointment(this.urlID).subscribe({
      next: (response) => {
        this.isCancelling = false;
        this.showCancelConfirmation = false;

        /*
         * If the backend returns the updated
         * appointment, use it immediately.
         */
        if (response?.appointment) {
          this.appointmentDetails = response.appointment;

          this.Services = (response.appointment as any)?.items || [];

          this.Salon = (response.appointment as any)?.salon || ({} as Salon);
        } else {
          /*
           * Otherwise reload from the API.
           */
          this.getAppointmentDetails(this.urlID);
        }
      },

      error: (error) => {
        this.isCancelling = false;

        console.error("Failed to cancel appointment:", error);

        const message =
          error?.error?.detail || "Unable to cancel this appointment.";

        window.alert(message);
      },
    });
  }

  /* =====================================================
     DIRECTIONS
  ====================================================== */

  getDirections(): void {
    const latitude = Number((this.Salon as any)?.latitude);

    const longitude = Number((this.Salon as any)?.longitude);

    let destination = "";

    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      destination = `${latitude},${longitude}`;
    } else {
      const address = [this.Salon?.address, this.Salon?.city]
        .filter(Boolean)
        .join(", ");

      if (!address) {
        return;
      }

      destination = address;
    }

    const url =
      "https://www.google.com/maps/dir/?api=1" +
      `&destination=${encodeURIComponent(destination)}`;

    window.open(url, "_blank", "noopener,noreferrer");
  }

  /* =====================================================
     NAVIGATION
  ====================================================== */

  navigateBack(): void {
    this.location.back();
  }

  /* =====================================================
     DATE / TIME
  ====================================================== */

  formatDate(dateString: string): string {
    if (!dateString || !dayjs(dateString).isValid()) {
      return "";
    }

    return dayjs(dateString).format("MMM D, YYYY");
  }

  formatTime(dateString: string): string {
    if (!dateString || !dayjs(dateString).isValid()) {
      return "";
    }

    return dayjs(dateString).format("h:mm A");
  }

  formatDatePart(dateString: string, format: string): string {
    if (!dateString || !dayjs(dateString).isValid()) {
      return "";
    }

    return dayjs(dateString).format(format);
  }

  /* =====================================================
     STATUS
  ====================================================== */

  statusClass(status: string): string {
    const normalized = String(status || "pending").toLowerCase();

    if (normalized === "completed") {
      return "status-completed";
    }

    if (normalized === "confirmed") {
      return "status-confirmed";
    }

    if (normalized === "cancelled") {
      return "status-cancelled";
    }

    return "status-pending";
  }
}
